/**
 * Utilitários para validação e normalização de WhatsApp
 */

/**
 * Lista de DDDs válidos no Brasil
 */
const VALID_DDDS = [
  11, 12, 13, 14, 15, 16, 17, 18, 19, // SP
  21, 22, 24, // RJ, ES
  27, 28, // ES
  31, 32, 33, 34, 35, 37, 38, // MG
  41, 42, 43, 44, 45, 46, // PR, SC
  47, 48, 49, // SC
  51, 53, 54, 55, // RS
  61, // DF
  62, 64, // GO
  63, // TO
  65, 66, // MT
  67, // MS
  68, // AC
  69, // RO
  71, 73, 74, 75, 77, // BA
  79, // SE
  81, 87, // PE
  82, // AL
  83, // PB
  84, // RN
  85, 88, // CE
  86, 89, // PI
  91, 93, 94, // PA
  92, 97, // AM
  95, // RR
  96, // AP
  98, 99 // MA
];

/**
 * Valida se um número de WhatsApp brasileiro é válido
 * Aceita formato antigo (10 dígitos: DDD + 8) e novo (11 dígitos: DDD + 9)
 * 
 * @param digits - String contendo apenas dígitos (já normalizado)
 * @returns true se o WhatsApp é válido, false caso contrário
 * 
 * @example
 * isValidWhatsApp('6291450653') // true (formato antigo: 10 dígitos)
 * isValidWhatsApp('629991450653') // true (formato novo: 11 dígitos)
 * isValidWhatsApp('123456') // false (menos de 10 dígitos)
 * isValidWhatsApp('0000000000') // false (apenas zeros)
 * isValidWhatsApp('99999999999') // false (DDD inválido)
 */
export function isValidWhatsApp(digits: string): boolean {
  // Deve ter 10 ou 11 dígitos (formato antigo ou novo)
  if (!digits || (digits.length !== 10 && digits.length !== 11)) {
    return false;
  }

  // Não pode ser apenas zeros
  if (digits === '0000000000' || digits === '00000000000') {
    return false;
  }

  // Não pode ser sequência repetida
  if (/^(\d)\1{9,10}$/.test(digits)) {
    return false;
  }

  // DDD deve ser válido (primeiros 2 dígitos)
  const ddd = parseInt(digits.substring(0, 2), 10);
  if (!VALID_DDDS.includes(ddd)) {
    return false;
  }

  // Número não pode começar com 0 ou 1 (após o DDD)
  const firstDigit = parseInt(digits[2], 10);
  if (firstDigit === 0 || firstDigit === 1) {
    return false;
  }

  return true;
}

/**
 * Normaliza um número de WhatsApp removendo caracteres não numéricos
 * e aplicando regras de normalização (remove DDI 55, completa DDD 62)
 * 
 * @param raw - String bruta do WhatsApp (pode conter formatação)
 * @returns String com apenas dígitos normalizados
 * 
 * @example
 * normalizeWhatsApp('(62) 99999-9999') // '62999999999'
 * normalizeWhatsApp('+55 62 99999-9999') // '62999999999'
 * normalizeWhatsApp('99999-9999') // '62999999999' (adiciona DDD 62)
 */
export function normalizeWhatsApp(raw: string): string {
  let digits = raw.replace(/\D/g, '');
  let hadDDI = false;
  
  // Remove DDI 55 se presente
  if (digits.startsWith('55') && digits.length > 11) {
    digits = digits.slice(2);
    hadDDI = true;
  }
  
  // Adiciona DDD 62 se tiver 10 dígitos E não veio de um número com DDI 55
  // (se veio com DDI 55, já tem o DDD correto após remover)
  if (digits.length === 10 && !hadDDI) {
    digits = `62${digits}`;
  }
  
  return digits;
}

/**
 * Valida e normaliza WhatsApp em uma única operação
 * 
 * @param raw - String bruta do WhatsApp
 * @returns Objeto com digits normalizados e se é válido
 * 
 * @example
 * validateAndNormalizeWhatsApp('(62) 99999-9999')
 * // { digits: '62999999999', isValid: true }
 */
export function validateAndNormalizeWhatsApp(raw: string): {
  digits: string;
  isValid: boolean;
} {
  const digits = normalizeWhatsApp(raw);
  const isValid = isValidWhatsApp(digits);
  
  return { digits, isValid };
}

